import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import TableSkeleton from "../../utils/TableSkeleton";
import { customStyles } from "../../utils/DataTableCustomStyle";
import * as Constants from "../../utils/Constants";
import * as UrlConstants from "../../utils/UrlConstants";
import {
    getClientIdOfUser,
    getAuthToken,
    showToast,
    generateRandomNumber,
    getLoggedInUserId,
} from "../../utils/utility";
import {
    buildPaginationParams,
    DEFAULT_PAGE_SIZE,
    extractPaginationHeadersFromFetch,
    PAGE_SIZE_OPTIONS,
} from "../../utils/paginationUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPlay,
    faCheck,
    faBan,
    faPen,
    faBoxesStacked,
} from "@fortawesome/free-solid-svg-icons";
import TripDeviceAssignment from "./TripDeviceAssignment";
import { hasPermission, ROLES, getRole } from "../../utils/rolePermissions";

const STATUS_COLORS = {
    SCHEDULED: { bg: "#e8f4fd", color: "#1a73e8", label: "Scheduled" },
    ACTIVE:    { bg: "#e6f4ea", color: "#137333", label: "Active" },
    COMPLETED: { bg: "#f1f3f4", color: "#5f6368", label: "Completed" },
    CANCELLED: { bg: "#fce8e6", color: "#c5221f", label: "Cancelled" },
};

const TRIP_TYPE_LABELS = {
    EXAM_PAPER:        "Exam Paper",
    CASH_IN_TRANSIT:   "Cash In Transit",
    ELECTION_MATERIAL: "Election Material",
    GENERAL:           "General",
};

const StatusBadge = ({ status }) => {
    const s = STATUS_COLORS[status] || { bg: "#f1f3f4", color: "#5f6368", label: status };
    return (
        <span style={{
            background: s.bg, color: s.color,
            borderRadius: "12px", padding: "2px 10px",
            fontSize: "12px", fontWeight: 600,
        }}>
            {s.label}
        </span>
    );
};

const TripList = ({ onEditTrip }) => {
    const [trips, setTrips]               = useState([]);
    const [loading, setLoading]           = useState(true);
    const [totalRows, setTotalRows]       = useState(0);
    const [perPage, setPerPage]           = useState(DEFAULT_PAGE_SIZE);
    const [currentPage, setCurrentPage]   = useState(1);
    const [actionLoading, setActionLoading] = useState(null);
    const [selectedTrip, setSelectedTrip] = useState(null);
    const [showDevices, setShowDevices]   = useState(false);

    const canManageTrips = hasPermission("trips_manage");
    const canManageLocks = hasPermission("trips_manageLocks");
    const showActions    = canManageTrips || canManageLocks;
    const isEmployee     = getRole() === ROLES.CLIENT_EMPLOYEE;

    useEffect(() => { fetchTrips(1, perPage); }, []);

    const fetchTrips = (page, size) => {
        setLoading(true);
        const userParam = isEmployee ? `&userId=${getLoggedInUserId()}` : '';
        const endpoint = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/${UrlConstants.TRIPS}?page=${page - 1}&size=${size}${userParam}`;
        fetch(endpoint, {
            headers: { Authorization: `${Constants.BEARER} ${getAuthToken()}` },
        })
            .then((res) => {
                const pagination = extractPaginationHeadersFromFetch(res);
                return res.json().then((data) => ({ data, pagination }));
            })
            .then(({ data, pagination }) => {
                const rows = data.responseData?.content || data.responseData || [];
                setTrips(rows);
                setTotalRows(pagination?.totalCount || rows.length);
            })
            .catch(() => showToast("Failed to load fleet trips", { type: "error" }))
            .finally(() => setLoading(false));
    };

    const doAction = (tripId, action) => {
        setActionLoading(`${tripId}-${action}`);
        const endpoint = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/${UrlConstants.TRIPS}/${tripId}/${action}`;
        fetch(endpoint, {
            method: "POST",
            headers: { Authorization: `${Constants.BEARER} ${getAuthToken()}` },
        })
            .then((res) => res.json())
            .then(() => {
                showToast(`Fleet trip ${action}d successfully`, { type: "success" });
                fetchTrips(currentPage, perPage);
            })
            .catch(() => showToast(`Failed to ${action} fleet trip`, { type: "error" }))
            .finally(() => setActionLoading(null));
    };

    const columns = [
        {
            name: "Name",
            selector: (row) => row.name,
            sortable: true,
            wrap: true,
        },
        {
            name: "Type",
            selector: (row) => TRIP_TYPE_LABELS[row.tripType] || row.tripType,
            sortable: true,
            width: "160px",
        },
        {
            name: "Status",
            cell: (row) => <StatusBadge status={row.status} />,
            width: "120px",
        },
        {
            name: "Scheduled Start",
            selector: (row) => row.scheduledStart ? new Date(row.scheduledStart).toLocaleString() : "—",
            sortable: true,
            width: "180px",
        },
        {
            name: "Scheduled End",
            selector: (row) => row.scheduledEnd ? new Date(row.scheduledEnd).toLocaleString() : "—",
            width: "180px",
        },
        {
            name: "Devices",
            cell: (row) => (
                <span style={{ fontWeight: 600 }}>
                    {row.deviceIds?.length || 0}
                </span>
            ),
            width: "80px",
            center: true,
        },
        // Actions column is omitted entirely for roles with no trip management permissions
        ...(showActions ? [{
            name: "Actions",
            width: "240px",
            cell: (row) => {
                const busy = (act) => actionLoading === `${row.id}-${act}`;
                return (
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                        {canManageTrips && (
                            <button className="btn-icon-sm" title="Edit fleet trip" onClick={() => onEditTrip(row)}>
                                <FontAwesomeIcon icon={faPen} />
                            </button>
                        )}
                        {canManageLocks && (
                            <button
                                className="btn-icon-sm"
                                title="Manage GPS locks for this fleet trip"
                                onClick={() => { setSelectedTrip(row); setShowDevices(true); }}>
                                <FontAwesomeIcon icon={faBoxesStacked} />
                            </button>
                        )}
                        {canManageTrips && row.status === "SCHEDULED" && (
                            <button
                                className="btn-sm btn-success"
                                disabled={busy("activate")}
                                onClick={() => doAction(row.id, "activate")}>
                                <FontAwesomeIcon icon={faPlay} /> Activate
                            </button>
                        )}
                        {canManageTrips && row.status === "ACTIVE" && (
                            <button
                                className="btn-sm btn-primary"
                                disabled={busy("complete")}
                                onClick={() => doAction(row.id, "complete")}>
                                <FontAwesomeIcon icon={faCheck} /> Complete
                            </button>
                        )}
                        {canManageTrips && (row.status === "SCHEDULED" || row.status === "ACTIVE") && (
                            <button
                                className="btn-sm btn-danger"
                                disabled={busy("cancel")}
                                onClick={() => doAction(row.id, "cancel")}>
                                <FontAwesomeIcon icon={faBan} /> Cancel
                            </button>
                        )}
                    </div>
                );
            },
        }] : []),
    ];

    return (
        <>
            <div className="overflow-hidden rounded-xl border border-ink-200 bg-white shadow-xs">
                <DataTable
                    columns={columns}
                    data={trips}
                    customStyles={customStyles}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationPerPage={perPage}
                    paginationRowsPerPageOptions={PAGE_SIZE_OPTIONS}
                    onChangePage={(page) => { setCurrentPage(page); fetchTrips(page, perPage); }}
                    onChangeRowsPerPage={(newSize, page) => { setPerPage(newSize); fetchTrips(page, newSize); }}
                    progressPending={loading}
                    progressComponent={<TableSkeleton />}
                    noDataComponent={
                        <div className="py-12 px-4 text-center text-sm text-ink-500">
                            No fleet trips yet. Use the <span className="font-medium text-ink-700">Create / Edit</span> tab to schedule one.
                        </div>
                    }
                />
            </div>

            {showDevices && selectedTrip && (
                <TripDeviceAssignment
                    trip={selectedTrip}
                    onClose={() => { setShowDevices(false); fetchTrips(currentPage, perPage); }}
                />
            )}
        </>
    );
};

export default TripList;
